import { createContext, useContext, useRef, useReducer, useState, useEffect, useCallback, useMemo } from "react";

import { Janus } from "lib/utils/janus";
import {
    getBboxDetectionsUrl,
    getJanusServerAddress,
    getThermalDetectionsUrl,
    isThermalCameraStream,
    joinUrl,
    pickDefaultStreamingSource,
    streamMatchesInferenceSource,
} from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";

import { StreamingState } from "lib/consts/streaming";
import { fetchInferSource, fetchYoloRecognition } from "lib/api/objectRecognition";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { useOrgFleet } from "./OrgFleetProvider";

export const JanusWebRTCContext = createContext();
JanusWebRTCContext.displayName = "JanusWebRTCContext";
export const useJanusWebRTC = () => useContext(JanusWebRTCContext);

const DEBUG_JANUS = false;
const debugLog = (...args) => {
    if (DEBUG_JANUS) console.log(...args);
};

/** Match rtsp_inferencing_module default TARGET_FPS (15). */
const BBOX_DETECTIONS_POLL_MS = 1000 / 15;
const BBOX_DETECTIONS_TIMEOUT_MS = 900;
const SESSION_RECONNECT_INITIAL_MS = 250;
const SESSION_RECONNECT_MAX_MS = 5000;
const MEDIA_STALL_GRACE_MS = 5000;
const STREAM_RECONNECT_INITIAL_MS = 2000;
const STREAM_RECONNECT_MAX_MS = 20000;

const InternalQuery = "FOR_INTERNAL_QUERY_USE_1259";
const StreamsToConnectOnStart = ["operator_stream", "PTZ Cam stream fallback"];
const PUBLIC_STUN = { urls: "stun:stun.l.google.com:19302" };
const JANUS_OPAQUE_ID_PREFIX = "r2c2";

/**
 * STUN served by the ARC itself (arc_gateway's coturn, stun-only, no relay,
 * no credentials). Derived from `window.location` for the same reason every
 * other direct-mode URL is: the gateway binds `:80` with no host matcher, so
 * `172.30.1.24`, `offline.r2c2.ai` and the device hostname must all work and
 * nothing may hard-code one of them.
 *
 * Note the port is explicit and NOT `window.location.port` — the page is on
 * 80, STUN is on 3478. Only the hostname is inherited.
 */
const localStunServer = () => {
    const { hostname } = window.location;
    return hostname ? { urls: `stun:${hostname}:3478` } : null;
};

/**
 * The two modes get DISJOINT ICE server lists. Neither one's server is of any
 * use to the other, so listing both would only add gathering latency:
 *
 *  - DIRECT: the on-device STUN server, and nothing else. This is an
 *    offline-first deployment — the public server is typically unreachable, so
 *    it contributes only a timeout before the host candidates win. It was the
 *    reason the whole list was effectively dead weight before.
 *  - FLEET: TURN on the arc_proxy host plus the public STUN server. The ARC's
 *    own STUN server is on the ARC's LAN and is not reachable from here.
 *
 * (This is the browser side, where `iceServers` is an array. Janus itself
 * accepts exactly one STUN server — see its janus.jcfg `nat` block.)
 */
const buildIceServers = (host, isFleetMode) => {
    if (!host || !isFleetMode) {
        const local = localStunServer();
        // No hostname to derive from is a degenerate case, not a reason to
        // reach for a public server the ARC probably cannot see: fall back to
        // host candidates only, which is what works on the LAN regardless.
        return local ? [local] : [];
    }
    return [
        { urls: `turn:${host}:3478`, username: "r2c2", credential: "r2c2" },
        PUBLIC_STUN,
    ];
};

const initialStreamState = {
    connected: false,
    streams: [],
    statuses: new Map(),
    versions: new Map(),
    activeStream: null,
};

const selectableStreams = (streams) =>
    streams.map((stream) => stream.description).filter(
        (description) => description && !description.match(/.*INTERNAL.*/i)
    );

const reconcileSelection = (state) => {
    const available = selectableStreams(state.streams);
    if (available.length === 0) return state;
    if (state.activeStream && available.includes(state.activeStream)) return state;
    const next = pickDefaultStreamingSource(available) ?? null;
    return next === state.activeStream ? state : { ...state, activeStream: next };
};

const streamReducer = (state, action) => {
    switch (action.type) {
        case "session-connected":
            return { ...state, connected: true };
        case "session-lost":
            return {
                ...state,
                connected: false,
                streams: [],
                statuses: new Map(),
                versions: new Map(),
            };
        case "session-closed":
            return { ...initialStreamState };
        case "session-streams":
            return reconcileSelection({ ...state, streams: action.streams });
        case "stream-status": {
            if (state.statuses.get(action.stream) === action.status) return state;
            const statuses = new Map(state.statuses);
            statuses.set(action.stream, action.status);
            return reconcileSelection({ ...state, statuses });
        }
        case "stream-track": {
            const versions = new Map(state.versions);
            versions.set(action.stream, (state.versions.get(action.stream) || 0) + 1);
            const statuses = new Map(state.statuses);
            statuses.set(action.stream, StreamingState.Okay);
            return reconcileSelection({ ...state, statuses, versions });
        }
        case "select-stream":
            return action.stream === state.activeStream
                ? state
                : { ...state, activeStream: action.stream };
        default:
            return state;
    }
};

const JanusWebRTCProvider = ({ children }) => {
    const isFleetMode = !!useOrgFleet();
    const isSecure = useIsHttpSecure() || isFleetMode;
    const arcId = useFleetArcId();
    const janus = useRef(null);
    // const janusServerAddress = getJanusServerAddress();
    const [janusServerHost, setJanusServerHost] = useState(null);
    const streamingPluginHandles = useRef(new Map());
    const reconnectingStreams = useRef(new Set());
    const mediaStallTimers = useRef(new Map());
    const streamReconnectTimers = useRef(new Map());
    const streamReconnectAttempts = useRef(new Map());
    const sessionGeneration = useRef(0);
    // Session-level (WebSocket) auto-reconnect. Distinct from per-stream
    // reconnect (reconnectingStreams): this re-establishes the whole Janus
    // session when the signalling WebSocket drops.
    const shouldStayConnected = useRef(false);
    const sessionReconnectTimer = useRef(null);
    const sessionReconnectAttempt = useRef(0);
    const connectRef = useRef(null);
    const [streamState, dispatch] = useReducer(streamReducer, initialStreamState);
    const {
        streams,
        statuses: streamStatuses,
        versions: streamVersions,
        connected: connectedToJanus,
        activeStream,
    } = streamState;
    const availableStreams = useMemo(() => selectableStreams(streams), [streams]);
    const setActiveStream = useCallback((stream) => {
        dispatch({ type: "select-stream", stream });
    }, []);

    const [showStreamingSource, setShowStreamingSource] = useState(false);
    const [fitScreen, setFitScreen] = useState(true);
    const [streamDetections, setStreamDetections] = useState(() => new Map());
    const [objectRecognitionEnabled, setObjectRecognitionEnabled] = useState(false);
    const objectRecognitionEnabledRef = useRef(objectRecognitionEnabled);
    objectRecognitionEnabledRef.current = objectRecognitionEnabled;
    const [inferenceSource, setInferenceSource] = useState("front");
    const inferenceSourceRef = useRef(inferenceSource);
    inferenceSourceRef.current = inferenceSource;
    const [pinnedPayloadStreams, setPinnedPayloadStreams] = useState([]);
    const activeStreamRef = useRef(activeStream);
    activeStreamRef.current = activeStream;

    /** Janus mount that should receive OR bboxes for the selected inference source. */
    const inferenceTargetStream = useMemo(
        () =>
            availableStreams.find((name) =>
                streamMatchesInferenceSource(name, inferenceSource)
            ) ?? null,
        [availableStreams, inferenceSource]
    );

    const refreshInferenceSource = useCallback((source) => {
        const next = source === "gimbal" ? "gimbal" : "front";
        setInferenceSource(next);
    }, []);

    const togglePinnedPayloadStream = useCallback((streamName) => {
        if (!streamName) return;
        setPinnedPayloadStreams((prev) => {
            if (prev.includes(streamName)) {
                return prev.filter((name) => name !== streamName);
            }
            // Do not pin the stream already shown as the main camera.
            if (streamName === activeStreamRef.current) return prev;
            return [...prev, streamName];
        });
    }, []);

    /** Swap main camera with a pinned floating stream (e.g. front ↔ gimbal). */
    const swapPinnedWithActive = useCallback((pinnedStreamName) => {
        const previousActive = activeStreamRef.current;
        if (!pinnedStreamName || !previousActive || pinnedStreamName === previousActive) {
            return;
        }
        setActiveStream(pinnedStreamName);
        setPinnedPayloadStreams((prev) => {
            const withoutPinned = prev.filter((name) => name !== pinnedStreamName);
            if (withoutPinned.includes(previousActive)) return withoutPinned;
            return [...withoutPinned, previousActive];
        });
    }, [setActiveStream]);

    // Drop pins for streams that are no longer listed by Janus, or that became the main feed.
    useEffect(() => {
        setPinnedPayloadStreams((prev) => {
            if (prev.length === 0) return prev;
            const next = prev.filter(
                (name) => availableStreams.includes(name) && name !== activeStream
            );
            return next.length === prev.length ? prev : next;
        });
    }, [availableStreams, activeStream]);

    const clearStreamDetections = useCallback((streamDescriptionOrID) => {
        setStreamDetections((prev) => {
            if (streamDescriptionOrID) {
                if (!prev.has(streamDescriptionOrID)) return prev;
                const next = new Map(prev);
                next.delete(streamDescriptionOrID);
                return next;
            }
            if (prev.size === 0) return prev;
            return new Map();
        });
    }, []);

    const updateStreamStatues = useCallback((stream, status) => {
        dispatch({ type: "stream-status", stream, status });
    }, []);

    const makeBitrateMonitorTask = useCallback(
        (streamDescriptionOrID) => {
            return setInterval(() => {
                let handle = streamingPluginHandles.current.get(streamDescriptionOrID);
                if (!handle) return;

                let bitrate = handle.getBitrate();
                bitrate = parseInt(bitrate.split()[0]);

                updateStreamStatues(
                    streamDescriptionOrID,
                    bitrate > 0.0 ? StreamingState.Okay : StreamingState.None
                );
            }, 2000);
        },
        [updateStreamStatues]
    );

    const updateStreamsList = useCallback(async () => {
        let pluginHandle = streamingPluginHandles.current.get(InternalQuery);
        if (!pluginHandle) return [];

        let body = { request: "list" };

        return new Promise((resolve) => {
            pluginHandle.send({
                message: body,
                success: (result) => {
                    if (!result || !("list" in result)) {
                        resolve([]);
                        return Janus.error("Got no response to our query for available streams");
                    }

                    let streams = [...result.list].sort((a, b) => (a.id < b.id ? -1 : 1));
                    dispatch({ type: "session-streams", streams });

                    resolve(streams);
                },
            });
        });
    }, []);

    const resolveStreamIDIn = useCallback((streams, streamDescriptionOrID) => {
        const stream = (streams || []).find(
            (candidate) =>
                streamDescriptionOrID === candidate.id ||
                streamDescriptionOrID === candidate.description
        );

        if (!stream) {
            Janus.error(
                `Unknown stream ${streamDescriptionOrID}, available streams are:`,
                streams
            );
            return null;
        }

        return stream.id;
    }, []);

    const startStream = useCallback(
        async (streamDescriptionOrID, knownStreams) => {
            Janus.log(`startStream is called for ${streamDescriptionOrID}`);

            let handle = streamingPluginHandles.current.get(streamDescriptionOrID);
            if (!handle)
                return Janus.error(`Unknown / not registered stream ${streamDescriptionOrID}`);

            // video_pipeline can recreate mountpoints and assign them new IDs.
            // Never trust an ID cached on an older plugin handle: resolve the
            // description against the list for this watch request.
            let streams = knownStreams;
            let resolvedStreamID = streams
                ? resolveStreamIDIn(streams, streamDescriptionOrID)
                : null;
            if (resolvedStreamID === null) {
                streams = await updateStreamsList();
                resolvedStreamID = resolveStreamIDIn(streams, streamDescriptionOrID);
            }
            if (resolvedStreamID === null)
                return Janus.error(
                    `Stream ${streamDescriptionOrID} not published on server side`
                );

            handle.resolvedStreamID = resolvedStreamID;

            let body = {
                request: "watch",
                id: parseInt(resolvedStreamID),
            };

            handle.send({ message: body });
            clearInterval(handle.bitrateMonitor);
            handle.bitrateMonitor = makeBitrateMonitorTask(streamDescriptionOrID);
        },
        [makeBitrateMonitorTask, resolveStreamIDIn, updateStreamsList]
    );

    const clearMediaStallTimer = useCallback((streamDescriptionOrID) => {
        const timer = mediaStallTimers.current.get(streamDescriptionOrID);
        if (timer) {
            clearTimeout(timer);
            mediaStallTimers.current.delete(streamDescriptionOrID);
        }
    }, []);

    const cancelStreamRecovery = useCallback(() => {
        sessionGeneration.current += 1;
        streamReconnectTimers.current.forEach((timer) => clearTimeout(timer));
        streamReconnectTimers.current.clear();
        streamReconnectAttempts.current.clear();
        reconnectingStreams.current.clear();
        mediaStallTimers.current.forEach((timer) => clearTimeout(timer));
        mediaStallTimers.current.clear();
    }, []);

    const stopStream = useCallback((streamDescriptionOrID) => {
        Janus.log(`stopStream is called for ${streamDescriptionOrID}`);
        clearMediaStallTimer(streamDescriptionOrID);

        let handle = streamingPluginHandles.current.get(streamDescriptionOrID);
        if (!handle) return Janus.error(`Unknown / not registered stream ${streamDescriptionOrID}`);

        try {
            handle.send({ message: { request: "stop" } });
            handle.hangup();
        } catch (error) {
            Janus.warn(`stopStream ${streamDescriptionOrID} teardown failed`, error);
        } finally {
            clearInterval(handle.bitrateMonitor);
            handle.mediaStream = null;
            streamingPluginHandles.current.delete(streamDescriptionOrID);
        }
    }, [clearMediaStallTimer]);

    const scheduleReconnect = useCallback(
        (streamDescriptionOrID, reason) => {
            if (reconnectingStreams.current.has(streamDescriptionOrID)) return;

            if (!janus.current || !shouldStayConnected.current) {
                updateStreamStatues(streamDescriptionOrID, StreamingState.Reconnecting);
                return;
            }

            reconnectingStreams.current.add(streamDescriptionOrID);
            const attempt = streamReconnectAttempts.current.get(streamDescriptionOrID) || 0;
            streamReconnectAttempts.current.set(streamDescriptionOrID, attempt + 1);
            const delay = Math.min(
                STREAM_RECONNECT_INITIAL_MS * (2 ** attempt),
                STREAM_RECONNECT_MAX_MS
            );
            Janus.warn(
                `streamingPluginHandle ${streamDescriptionOrID} ${reason}, reconnecting in ${delay}ms...`
            );
            stopStream(streamDescriptionOrID);
            updateStreamStatues(streamDescriptionOrID, StreamingState.Reconnecting);
            const generation = sessionGeneration.current;
            streamReconnectTimers.current.set(
                streamDescriptionOrID,
                setTimeout(() => {
                    streamReconnectTimers.current.delete(streamDescriptionOrID);
                    reconnectingStreams.current.delete(streamDescriptionOrID);
                    if (
                        generation !== sessionGeneration.current ||
                        !janus.current ||
                        !shouldStayConnected.current
                    ) return;
                    attachStreamingPlugin(streamDescriptionOrID);
                }, delay)
            );
        },
        [stopStream, updateStreamStatues]
    );

    const attachStreamingPlugin = useCallback(
        async (streamDescriptionOrID, startStreamAfterAttach = true, knownStreams) => {
            if (reconnectingStreams.current.has(streamDescriptionOrID)) {
                Janus.log(`streamingPluginHandle ${streamDescriptionOrID} reconnect already pending, skipping attach`);
                return;
            }
            // The session can be torn down between scheduling an attach and
            // running it. Attaching to a destroyed session throws inside the
            // promise executor below, surfacing as an unhandled rejection.
            if (!janus.current) {
                Janus.warn(`attach ${streamDescriptionOrID} skipped: no Janus session`);
                return;
            }
            if (streamingPluginHandles.current.has(streamDescriptionOrID)) {
                Janus.log(`streamingPluginHandle ${streamDescriptionOrID} already attached, skipping`);
                return;
            }
            const attachGeneration = sessionGeneration.current;
            return new Promise((resolve) => {
                janus.current.attach({
                    plugin: "janus.plugin.streaming",
                    // Janus forwards opaque_id to its TURN REST backend as the
                    // `username` query parameter. arc_runtime_config uses this
                    // per-handle mode tag to withhold fleet TURN servers from
                    // direct/LAN PeerConnections without changing the global
                    // TURN config used by simultaneous fleet viewers.
                    opaqueId: `${JANUS_OPAQUE_ID_PREFIX}-${arcId ? "fleet" : "direct"}:${streamDescriptionOrID}`,
                    success: (pluginHandle) => {
                        if (
                            attachGeneration !== sessionGeneration.current ||
                            !janus.current ||
                            !shouldStayConnected.current
                        ) {
                            try {
                                pluginHandle.detach();
                            } catch {
                                /* session already gone */
                            }
                            resolve();
                            return;
                        }
                        Janus.log(`streamingPluginHandle ${streamDescriptionOrID} created`);

                        streamingPluginHandles.current.set(streamDescriptionOrID, pluginHandle);
                        updateStreamStatues(streamDescriptionOrID, StreamingState.None);

                        pluginHandle.resolvedStreamID = null;
                        pluginHandle.mediaStream = null;

                        if (startStreamAfterAttach) startStream(streamDescriptionOrID, knownStreams);

                        resolve();
                    },
                    error: (error) => {
                        Janus.error(
                            `streamingPluginHandle ${streamDescriptionOrID} creation failed with error: `,
                            error
                        );
                        resolve();
                    },
                    iceState: (state) => {
                        Janus.log(
                            `streamingPluginHandle ${streamDescriptionOrID} ICE state changed to ${state}`
                        );
                        if (state === "failed" || state === "disconnected") {
                            scheduleReconnect(streamDescriptionOrID, `ICE ${state}`);
                        }
                    },
                    webrtcState: (on) => {
                        Janus.log(
                            `streamingPluginHandle ${streamDescriptionOrID} WebRTC PeerConnection is ${on ? "up" : "down"
                            } now`
                        );
                        if (!on) {
                            scheduleReconnect(streamDescriptionOrID, "PeerConnection down");
                        }
                    },
                    slowLink: (uplink, lost, mid) => {
                        Janus.warn(
                            `streamingPluginHandle ${streamDescriptionOrID} reports problems ${uplink ? "sending" : "receiving"
                            } packets)`
                        );
                        updateStreamStatues(streamDescriptionOrID, StreamingState.Slow);
                    },
                    onmessage: (msg, jsep) => {
                        if (attachGeneration !== sessionGeneration.current) return;
                        Janus.debug(
                            `streamingPluginHandle ${streamDescriptionOrID} got a message:`,
                            msg
                        );

                        let result = msg["result"];
                        if (result) {
                            if (result["status"]) {
                                let status = result["status"];
                                if (status === "starting")
                                    Janus.log(
                                        `streamingPluginHandle ${streamDescriptionOrID} stream starting, please wait...`
                                    );
                                else if (status === "started")
                                    Janus.log(
                                        `streamingPluginHandle ${streamDescriptionOrID} stream started`
                                    );
                                else if (status === "stopped") {
                                    Janus.log(
                                        `streamingPluginHandle ${streamDescriptionOrID} stream stopped from server side`
                                    );
                                    stopStream(streamDescriptionOrID);
                                }
                            } else if (msg["streaming"] === "event") {
                                let subStream = result["substream"];
                                let temporal = result["temporal"];
                                if (
                                    (subStream !== null && subStream !== undefined) ||
                                    (temporal !== null && temporal !== undefined)
                                )
                                    Janus.warn(
                                        "Stream has Simulcast but Simulcast UI not implemented"
                                    );

                                let spatial = result["spatial_layer"];
                                temporal = result["temporal_layer"];
                                if (
                                    (spatial !== null && spatial !== undefined) ||
                                    (temporal !== null && temporal !== undefined)
                                )
                                    Janus.warn("Stream has SVC but SVC UI not implemented");
                            }
                        } else if (msg["error"])
                            return Janus.error(
                                `streamingPluginHandle ${streamDescriptionOrID} get error`,
                                msg
                            );

                        if (jsep) {
                            Janus.debug(
                                `streamingPluginHandle ${streamDescriptionOrID} Handling SDP from server`,
                                jsep
                            );
                            const handle = streamingPluginHandles.current.get(streamDescriptionOrID);
                            if (!handle) return;
                            let stereo = jsep.sdp.indexOf("stereo=1") !== -1;
                            // Offer from the plugin, let's answer
                            handle.createAnswer({
                                jsep: jsep,
                                // We only specify data channels here, as this way in
                                // case they were offered we'll enable them. Since we
                                // don't mention audio or video tracks, we auto accept them
                                // as recvonly (since we won't capture anything ourselves)
                                tracks: [{ type: "data" }],
                                customizeSdp: (jsep) => {
                                    if (stereo && jsep.sdp.indexOf("stereo=1") === -1) {
                                        // Make sure that our offer contains stereo too
                                        jsep.sdp = jsep.sdp.replace(
                                            "useinbandfec=1",
                                            "useinbandfec=1;stereo=1"
                                        );
                                    }
                                },
                                success: (jsep) => {
                                    Janus.debug("Got SDP!", jsep);
                                    let body = { request: "start" };

                                    const currentHandle =
                                        streamingPluginHandles.current.get(streamDescriptionOrID);
                                    if (currentHandle) {
                                        currentHandle.send({ message: body, jsep: jsep });
                                    }
                                },
                                error: (error) => Janus.error("WebRTC error:", error),
                            });
                        }
                    },
                    onremotetrack: (track, mid, on) => {
                        if (attachGeneration !== sessionGeneration.current) return;
                        let handle = streamingPluginHandles.current.get(streamDescriptionOrID);

                        debugLog("onremotetrack handle: ", handle);

                        Janus.debug(
                            `streamingPluginHandle ${streamDescriptionOrID} Remote track (mid=${mid}) ${on ? "added" : "removed"
                            }`,
                            track
                        );

                        if (track.kind === "audio") {
                            Janus.debug("audio track not treated yet");
                            return;
                        }

                        if (track.kind === "video") {
                            //FIXME? we assume the source only has 1 video track

                            if (on) {
                                Janus.log(
                                    `streamingPluginHandle ${streamDescriptionOrID} Created remote track:`,
                                    mid
                                );

                                // Fleet/TURN tracks can be announced while
                                // initially muted. Attach them immediately so
                                // the video element is ready when RTP starts;
                                // Janus's later unmute callback is not
                                // guaranteed to be forwarded to us.
                                let stream = handle.mediaStream;
                                if (!stream) stream = new MediaStream();
                                const isNewTrack = !stream.getTracks().includes(track);
                                if (isNewTrack) stream.addTrack(track);
                                handle.mediaStream = stream;

                                if (isNewTrack) {
                                    dispatch({ type: "stream-track", stream: streamDescriptionOrID });
                                }

                                if (!track.muted) {
                                    clearMediaStallTimer(streamDescriptionOrID);
                                    reconnectingStreams.current.delete(streamDescriptionOrID);
                                    // Media is flowing: this stream's recovery
                                    // backoff starts from scratch next time.
                                    streamReconnectAttempts.current.delete(streamDescriptionOrID);
                                }
                            } else if (!on) {
                                scheduleReconnect(streamDescriptionOrID, `remote track removed (mid=${mid})`);
                            }
                        }
                    },
                    ondetached: () => {
                        if (attachGeneration !== sessionGeneration.current) return;
                        clearMediaStallTimer(streamDescriptionOrID);
                        const handle = streamingPluginHandles.current.get(streamDescriptionOrID);
                        if (handle) clearInterval(handle.bitrateMonitor);
                        streamingPluginHandles.current.delete(streamDescriptionOrID);
                        if (streamDescriptionOrID !== InternalQuery) {
                            scheduleReconnect(streamDescriptionOrID, "handle detached");
                        }
                    },
                    mediaState: (type, receiving, mid) => {
                        if (attachGeneration !== sessionGeneration.current) return;
                        if (type !== "video") return;

                        Janus.log(
                            `streamingPluginHandle ${streamDescriptionOrID} ${receiving ? "is" : "stopped"} receiving video (mid=${mid})`
                        );

                        const mediaStallTimer = mediaStallTimers.current.get(streamDescriptionOrID);
                        if (receiving) {
                            clearMediaStallTimer(streamDescriptionOrID);
                            updateStreamStatues(streamDescriptionOrID, StreamingState.Okay);
                            return;
                        }

                        updateStreamStatues(streamDescriptionOrID, StreamingState.None);
                        if (mediaStallTimer) return;

                        mediaStallTimers.current.set(
                            streamDescriptionOrID,
                            setTimeout(() => {
                                mediaStallTimers.current.delete(streamDescriptionOrID);
                                scheduleReconnect(streamDescriptionOrID, "media stopped");
                            }, MEDIA_STALL_GRACE_MS)
                        );
                    },
                    oncleanup: () => {
                        if (attachGeneration !== sessionGeneration.current) return;
                        Janus.log(" ::: Got a cleanup notification :::");
                        clearMediaStallTimer(streamDescriptionOrID);

                        updateStreamStatues(streamDescriptionOrID, StreamingState.None);
                    },
                    ondata: (data) => {
                        if (attachGeneration !== sessionGeneration.current) return;
                        if (
                            !objectRecognitionEnabledRef.current ||
                            !streamMatchesInferenceSource(
                                streamDescriptionOrID,
                                inferenceSourceRef.current,
                            )
                        ) {
                            return;
                        }
                        try {
                            const text =
                                typeof data === "string" ? data : new TextDecoder().decode(data);
                            const parsed = JSON.parse(text);
                            if (
                                parsed &&
                                typeof parsed.w === "number" &&
                                typeof parsed.h === "number" &&
                                Array.isArray(parsed.boxes)
                            ) {
                                setStreamDetections((prev) => {
                                    const next = new Map(prev);
                                    next.set(streamDescriptionOrID, {
                                        w: parsed.w,
                                        h: parsed.h,
                                        boxes: parsed.boxes,
                                    });
                                    return next;
                                });
                            }
                        } catch {
                            /* ignore non-JSON datachannel noise */
                        }
                    },
                });
            });
        },
        [
            arcId,
            clearMediaStallTimer,
            startStream,
            stopStream,
            updateStreamStatues,
            scheduleReconnect,
        ]
    );

    // Re-establish the whole Janus session after the signalling WebSocket drops.
    // janus.js fires the top-level `error` callback both on a lost connection
    // ("Lost connection to the server") and on a failed initial connect, so this
    // is the single hook for either case. Guarded so concurrent error/destroyed
    // callbacks don't stack timers.
    const scheduleSessionReconnect = useCallback((reason) => {
        if (!shouldStayConnected.current) return; // intentional teardown
        if (sessionReconnectTimer.current) return; // already scheduled
        const delay = Math.min(
            SESSION_RECONNECT_INITIAL_MS * (2 ** sessionReconnectAttempt.current),
            SESSION_RECONNECT_MAX_MS
        );
        sessionReconnectAttempt.current += 1;
        Janus.warn(`Janus session ${reason}, reconnecting in ${delay}ms...`);
        // Drop stale handles so streaming plugins re-attach fresh on the new session.
        // Clear monitors before dropping the map: otherwise their callbacks keep
        // publishing statuses for handles belonging to the old session.
        streamingPluginHandles.current.forEach((handle) => {
            clearInterval(handle.bitrateMonitor);
        });
        streamingPluginHandles.current.clear();
        cancelStreamRecovery();
        dispatch({ type: "session-lost" });
        janus.current = null;
        sessionReconnectTimer.current = setTimeout(() => {
            sessionReconnectTimer.current = null;
            if (shouldStayConnected.current && connectRef.current) connectRef.current();
        }, delay);
    }, [cancelStreamRecovery]);

    const connect = useCallback(() => {
        debugLog("JanusWebRTCProvider connect to", janusServerHost);
        debugLog("JanusWebRTCProvider: arcId:", arcId);

        shouldStayConnected.current = true;

        Janus.init({
            debug: DEBUG_JANUS,
            // Janus's WebSocket server URL already carries the fleet path
            // prefix, and the vendored client preserves it for follow-up
            // session/handle requests. No ?arc= fetch wrapper is needed.
            // eslint-disable-next-line react-hooks/rules-of-hooks -- Janus API method, not a React hook
            dependencies: Janus.useDefaultDependencies(),
            callback: () => {
                const server = getJanusServerAddress(janusServerHost, isSecure, arcId);
                if (!server) {
                    console.warn("Janus unavailable: fleet proxy host is missing");
                    dispatch({ type: "session-lost" });
                    return;
                }
                console.log('janus server address', server)

                janus.current = new Janus({
                    server,
                    iceServers: buildIceServers(janusServerHost, !!arcId),
                    // Force all media through coturn to test TURN relay. Fleet mode
                    // only — ARC mode has no TURN server, so relay would kill streaming.
                    iceTransportPolicy: arcId ? "relay" : "all",
                    success: async () => {
                        sessionReconnectAttempt.current = 0;
                        Janus.log('connected to janus!')
                        dispatch({ type: "session-connected" });
                        await attachStreamingPlugin(InternalQuery, false);
                        const streams = await updateStreamsList();
                        streams.forEach(
                            (stream) => attachStreamingPlugin(stream.description, true, streams)
                        );
                    },
                    error: (error) => {
                        Janus.error(error);
                        scheduleSessionReconnect("connection error");
                    },
                    destroyed: () => {
                        scheduleSessionReconnect("session destroyed");
                    },
                });
            },
        });
    }, [attachStreamingPlugin, janusServerHost, updateStreamsList, isSecure, arcId, scheduleSessionReconnect]);

    // Keep a ref to the latest connect() so the reconnect timer (which is stable)
    // always calls the current closure rather than a stale one.
    useEffect(() => {
        connectRef.current = connect;
    }, [connect]);

    const disconnect = useCallback(() => {
        debugLog("JanusWebRTCProvider disconnect from", janusServerHost);

        // Intentional teardown: stop the auto-reconnect loop before closing.
        shouldStayConnected.current = false;
        sessionReconnectAttempt.current = 0;
        if (sessionReconnectTimer.current) {
            clearTimeout(sessionReconnectTimer.current);
            sessionReconnectTimer.current = null;
        }

        Array.from(streamingPluginHandles.current.keys()).forEach((stream) =>
            stopStream(stream)
        );

        if (janus.current) {
            try {
                janus.current.destroy({ notifyDestroyed: false });
            } catch {
                /* already gone */
            }
            janus.current = null;
        }

        cancelStreamRecovery();
        dispatch({ type: "session-closed" });
    }, [cancelStreamRecovery, janusServerHost, stopStream]);

    const getStream = useCallback((streamDescriptionOrID) => {
        let handle = streamingPluginHandles.current.get(streamDescriptionOrID);
        if (!handle)
            return Janus.error(
                `getStream failed as stream '${streamDescriptionOrID}' do not exist`
            );

        if (!handle.mediaStream)
            return Janus.error(
                `getStream failed as stream '${streamDescriptionOrID}' exist but has no video track`
            );

        return handle.mediaStream;
    }, []);

    const attachMediaStreamToElem = useCallback(
        (element, streamDescriptionOrID) => {
            try {

                let stream = streamDescriptionOrID ? getStream(streamDescriptionOrID) : null;
                if (!stream) {
                    if (element.srcObject) element.srcObject = null;
                    return;
                }

                const alreadyAttached = element.srcObject === stream;
                // Skip redundant re-assignment: setting srcObject to the same
                // MediaStream still tears down and re-negotiates the <video> render
                // pipeline, causing visible flicker. The attach effect re-fires every
                // ~2s because the bitrate monitor replaces the streamStatuses Map ref.
                if (!alreadyAttached) element.srcObject = stream;

                if (element.paused && typeof element.play === "function") {
                    const playPromise = element.play();
                    if (playPromise?.catch) {
                        playPromise.catch((error) =>
                            Janus.warn(
                                `attachMediaStreamToElem ${streamDescriptionOrID} playback did not start`,
                                error
                            )
                        );
                    }
                }
            } catch (e) {
                Janus.warn(`attachMediaStreamToElem ${streamDescriptionOrID} failed`, e);
            }
        },
        [getStream]
    );

    useEffect(() => {
        if (!janusServerHost) return;

        if (arcId) {
            const turnBase = gatewayServiceBaseUrl("turn", arcId, janusServerHost);
            if (turnBase) {
                const turnUrl = joinUrl(turnBase, "/turn");
                authedFetch(turnUrl, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        iceServers: buildIceServers(janusServerHost, !!arcId).filter((s) =>
                            [].concat(s.urls).some((u) => u.startsWith("turn:") || u.startsWith("turns:"))
                        ),
                    }),
                })
                    .then((r) => r.json())
                    .then((d) => debugLog("TURN config pushed to ARC:", d))
                    .catch((e) => console.warn("Failed to push TURN config:", e));
            }
        }

        connect();

        return () => disconnect();
    }, [janusServerHost, arcId, isSecure]);

    // Sync overlay gate from the robot as soon as the ARC host is known.
    // Previously only ObjectRecognitionSettingsPanel did this on mount, so
    // bboxes never appeared on the control feed until that settings tab was opened.
    useEffect(() => {
        if (!janusServerHost) return;
        let cancelled = false;
        (async () => {
            try {
                const { enabled } = await fetchYoloRecognition(
                    janusServerHost,
                    isSecure,
                    arcId
                );
                if (!cancelled) setObjectRecognitionEnabled(Boolean(enabled));
            } catch {
                if (!cancelled) setObjectRecognitionEnabled(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [janusServerHost, isSecure, arcId]);

    useEffect(() => {
        if (!janusServerHost) return;
        let cancelled = false;
        (async () => {
            try {
                const payload = await fetchInferSource(
                    janusServerHost,
                    isSecure,
                    arcId
                );
                if (!cancelled) {
                    refreshInferenceSource(payload.source);
                }
            } catch {
                if (!cancelled) refreshInferenceSource("front");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [janusServerHost, isSecure, arcId, refreshInferenceSource]);

    useEffect(() => {
        if (objectRecognitionEnabled) return;
        if (inferenceTargetStream) {
            clearStreamDetections(inferenceTargetStream);
        }
    }, [objectRecognitionEnabled, inferenceTargetStream, clearStreamDetections]);

    // Bboxes: poll inferencer HTTP JSON for the Janus mount matching inference source
    // (main view and/or pinned popup — not gated on activeStream).
    useEffect(() => {
        if (!janusServerHost || !objectRecognitionEnabled || !inferenceTargetStream) {
            return;
        }

        let cancelled = false;
        const currentStream = inferenceTargetStream;
        const tick = async () => {
            if (cancelled) return;
            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => {
                controller.abort();
            }, BBOX_DETECTIONS_TIMEOUT_MS);
            try {
                const detectionsUrl = getBboxDetectionsUrl(
                    janusServerHost,
                    isSecure,
                    arcId
                );
                if (!detectionsUrl) return;
                const res = await authedFetch(detectionsUrl, {
                    cache: "no-store",
                    signal: controller.signal,
                });
                if (cancelled) return;
                if (!res.ok) {
                    clearStreamDetections(currentStream);
                    return;
                }
                const parsed = await res.json();
                if (cancelled) return;
                if (
                    parsed &&
                    typeof parsed.w === "number" &&
                    typeof parsed.h === "number" &&
                    Array.isArray(parsed.boxes)
                ) {
                    setStreamDetections((prev) => {
                        const next = new Map(prev);
                        next.set(currentStream, {
                            w: parsed.w,
                            h: parsed.h,
                            boxes: parsed.boxes,
                        });
                        return next;
                    });
                } else {
                    clearStreamDetections(currentStream);
                }
            } catch {
                if (!cancelled) {
                    clearStreamDetections(currentStream);
                }
            } finally {
                clearTimeout(timeoutId);
                if (!cancelled) {
                    window.setTimeout(tick, BBOX_DETECTIONS_POLL_MS);
                }
            }
        };

        tick();
        return () => {
            cancelled = true;
            clearStreamDetections(currentStream);
        };
    }, [
        janusServerHost,
        inferenceTargetStream,
        isSecure,
        arcId,
        objectRecognitionEnabled,
        clearStreamDetections,
    ]);

    // Thermal hotspot bboxes: poll thermal_detection whenever the active stream is thermal.
    useEffect(() => {
        if (!janusServerHost || !isThermalCameraStream(activeStream)) {
            return;
        }

        let cancelled = false;
        const currentStream = activeStream;
        const tick = async () => {
            if (cancelled) return;
            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => {
                controller.abort();
            }, BBOX_DETECTIONS_TIMEOUT_MS);
            try {
                const detectionsUrl = getThermalDetectionsUrl(
                    janusServerHost,
                    isSecure,
                    arcId
                );
                if (!detectionsUrl) return;
                const res = await authedFetch(detectionsUrl, {
                    cache: "no-store",
                    signal: controller.signal,
                });
                if (cancelled) return;
                if (!res.ok) {
                    clearStreamDetections(currentStream);
                    return;
                }
                const parsed = await res.json();
                if (cancelled) return;
                if (
                    parsed &&
                    typeof parsed.w === "number" &&
                    typeof parsed.h === "number" &&
                    Array.isArray(parsed.boxes)
                ) {
                    setStreamDetections((prev) => {
                        const next = new Map(prev);
                        next.set(currentStream, {
                            w: parsed.w,
                            h: parsed.h,
                            boxes: parsed.boxes,
                        });
                        return next;
                    });
                } else {
                    clearStreamDetections(currentStream);
                }
            } catch {
                if (!cancelled) {
                    clearStreamDetections(currentStream);
                }
            } finally {
                clearTimeout(timeoutId);
                if (!cancelled) {
                    window.setTimeout(tick, BBOX_DETECTIONS_POLL_MS);
                }
            }
        };

        tick();
        return () => {
            cancelled = true;
            clearStreamDetections(currentStream);
        };
    }, [janusServerHost, activeStream, isSecure, arcId, clearStreamDetections]);

    return (
        <JanusWebRTCContext.Provider
            value={{
                setJanusServerHost,
                connectedToJanus,
                availableStreams,
                // Janus descriptions are the keys; Arm Cam label is resolved in streamDisplayLabel.
                streamDisplayNames: new Map(
                    availableStreams.map((name) => [name, name]),
                ),
                streamStatuses,
                streamVersions,
                activeStream,
                setActiveStream,
                getStream,
                attachMediaStreamToElem,
                streamDetections,
                objectRecognitionEnabled,
                setObjectRecognitionEnabled,
                inferenceSource,
                refreshInferenceSource,
                clearStreamDetections,
                pinnedPayloadStreams,
                togglePinnedPayloadStream,
                swapPinnedWithActive,
                showStreamingSource,
                setShowStreamingSource,
                fitScreen,
                setFitScreen,
            }}
        >
            {children}
        </JanusWebRTCContext.Provider>
    );
};

export default JanusWebRTCProvider;
